import { ulid } from "ulid";

import { extractIdsFromCitation, type Citation } from "../../citations";
import type { PublicResearchToolName } from "./types";

export const PLANNING_EVIDENCE_VERSION = 1 as const;

export interface PlanningEvidenceCall {
  readonly evidenceId: string;
  readonly tool: PublicResearchToolName;
  readonly query: string;
  readonly answer: string;
  readonly sessionId?: string;
  readonly capturedAt: string;
  readonly answerChars: number;
  /** Citation ids present in the captured answer. */
  readonly citationIds: readonly string[];
  /** Citation ids in the answer that did not have a retained source envelope. */
  readonly missingCitationIds?: readonly string[];
}

export interface PlanningEvidencePack {
  readonly version: typeof PLANNING_EVIDENCE_VERSION;
  readonly reviewOperationId: string;
  readonly capturedAt: string;
  readonly skillIds?: readonly string[];
  readonly guidanceChars?: number;
  readonly calls: readonly PlanningEvidenceCall[];
  readonly citations: readonly Citation[];
  readonly count: number;
  readonly citationCount: number;
  readonly totalAnswerChars: number;
  readonly sizeBytes: number;
  readonly truncated: boolean;
}

export interface AppendPlanningEvidenceInput {
  readonly reviewOperationId: string;
  readonly current?: PlanningEvidencePack;
  readonly evidenceId?: string;
  readonly tool: PublicResearchToolName;
  readonly query: string;
  readonly answer: string;
  readonly sessionId?: string;
  readonly citations?: readonly Citation[];
  readonly capturedAt?: string;
}

function utf8Bytes(value: string): number[] {
  const bytes: number[] = [];
  for (let index = 0; index < value.length; index += 1) {
    let codePoint = value.charCodeAt(index);
    if (codePoint >= 0xd800 && codePoint <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        codePoint = 0x10000 + ((codePoint - 0xd800) << 10) + (next - 0xdc00);
        index += 1;
      } else {
        codePoint = 0xfffd;
      }
    } else if (codePoint >= 0xdc00 && codePoint <= 0xdfff) {
      codePoint = 0xfffd;
    }

    if (codePoint <= 0x7f) {
      bytes.push(codePoint);
    } else if (codePoint <= 0x7ff) {
      bytes.push(0xc0 | (codePoint >>> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint <= 0xffff) {
      bytes.push(
        0xe0 | (codePoint >>> 12),
        0x80 | ((codePoint >>> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >>> 18),
        0x80 | ((codePoint >>> 12) & 0x3f),
        0x80 | ((codePoint >>> 6) & 0x3f),
        0x80 | (codePoint & 0x3f)
      );
    }
  }
  return bytes;
}

function unicodeCharacterCount(value: string): number {
  return Array.from(value).length;
}

const UNDERLINE_OPEN_TAG = /<u\b([^>]*)>/gi;
const ID_ATTRIBUTE = /\bid\s*=\s*(?:"([^"]+)"|'([^']+)')/i;

export function extractCitationIdsFromPlanningAnswer(answer: string): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  const candidates = [
    answer,
    answer.replace(
      /&(lt|gt|quot|#39|apos|amp);/g,
      (entity, name: string) =>
        ({
          lt: "<",
          gt: ">",
          quot: '"',
          "#39": "'",
          apos: "'",
          amp: "&",
        })[name] ?? entity
    ),
  ];
  for (const candidate of candidates) {
    UNDERLINE_OPEN_TAG.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = UNDERLINE_OPEN_TAG.exec(candidate)) !== null) {
      const idMatch = ID_ATTRIBUTE.exec(match[1]);
      const id = (idMatch?.[1] ?? idMatch?.[2] ?? "").trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

function cloneAndFreeze<T>(value: T): T {
  const clone = JSON.parse(JSON.stringify(value)) as T;
  const freeze = (entry: unknown): void => {
    if (!entry || typeof entry !== "object" || Object.isFrozen(entry)) return;
    for (const child of Object.values(entry as Record<string, unknown>)) freeze(child);
    Object.freeze(entry);
  };
  freeze(clone);
  return clone;
}

function selectRetainedCitations(
  candidates: readonly Citation[],
  calls: readonly PlanningEvidenceCall[]
): {
  citations: readonly Citation[];
  availableCitationIds: ReadonlySet<string>;
} {
  const referencedIds = new Set(calls.flatMap((call) => call.citationIds));
  if (referencedIds.size === 0) {
    return {
      citations: Object.freeze([]),
      availableCitationIds: new Set(),
    };
  }

  const citations: Citation[] = [];
  const seenIds = new Set<string>();
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const ids = extractIdsFromCitation(candidate);
    const relevantIds = ids.filter((id) => referencedIds.has(id));
    if (relevantIds.length === 0) continue;
    if (relevantIds.every((id) => seenIds.has(id))) continue;
    citations.push(cloneAndFreeze(candidate));
    for (const id of ids) {
      if (referencedIds.has(id)) seenIds.add(id);
    }
  }
  return {
    citations: Object.freeze(citations),
    availableCitationIds: seenIds,
  };
}

function annotateMissingCitationEnvelopes(
  calls: readonly PlanningEvidenceCall[],
  availableCitationIds: ReadonlySet<string>
): readonly PlanningEvidenceCall[] {
  return Object.freeze(
    calls.map((call) => {
      const missingCitationIds = call.citationIds.filter((id) => !availableCitationIds.has(id));
      if (missingCitationIds.length === 0) return call;
      return {
        ...call,
        missingCitationIds: Object.freeze(missingCitationIds),
      };
    })
  );
}

function retainReferencedCitationEnvelopes(
  candidates: readonly Citation[],
  calls: readonly PlanningEvidenceCall[]
): {
  calls: readonly PlanningEvidenceCall[];
  citations: readonly Citation[];
} {
  const selected = selectRetainedCitations(candidates, calls);
  return {
    calls: annotateMissingCitationEnvelopes(calls, selected.availableCitationIds),
    citations: selected.citations,
  };
}

function serializedSizeWithFixedPoint(value: Omit<PlanningEvidencePack, "sizeBytes">): number {
  let sizeBytes = 0;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const next = utf8Bytes(JSON.stringify({ ...value, sizeBytes })).length;
    if (next === sizeBytes) return next;
    sizeBytes = next;
  }
  return sizeBytes;
}

function finalizePlanningEvidencePack(input: {
  reviewOperationId: string;
  capturedAt: string;
  skillIds?: readonly string[];
  guidanceChars?: number;
  calls: readonly PlanningEvidenceCall[];
  citations: readonly Citation[];
  truncated: boolean;
}): PlanningEvidencePack {
  const calls = Object.freeze(
    input.calls.map((call) => {
      const { missingCitationIds, ...callWithoutMissing } = call;
      return Object.freeze({
        ...callWithoutMissing,
        citationIds: Object.freeze([...call.citationIds]),
        ...(missingCitationIds?.length
          ? { missingCitationIds: Object.freeze([...missingCitationIds]) }
          : undefined),
      });
    })
  );
  const citations = Object.freeze([...input.citations]);
  const totalAnswerChars = calls.reduce((total, call) => total + call.answerChars, 0);
  const withoutSize: Omit<PlanningEvidencePack, "sizeBytes"> = {
    version: PLANNING_EVIDENCE_VERSION,
    reviewOperationId: input.reviewOperationId,
    capturedAt: input.capturedAt,
    ...(input.skillIds?.length ? { skillIds: Object.freeze([...input.skillIds]) } : undefined),
    ...(typeof input.guidanceChars === "number" ? { guidanceChars: input.guidanceChars } : undefined),
    calls,
    citations,
    count: calls.length,
    citationCount: citations.length,
    totalAnswerChars,
    truncated: input.truncated,
  };
  return Object.freeze({
    ...withoutSize,
    sizeBytes: serializedSizeWithFixedPoint(withoutSize),
  });
}

export function createPlanningEvidenceId(): string {
  return `research-${ulid()}`;
}

export function createReviewOperationId(): string {
  return `review-${ulid()}`;
}

export function appendPlanningEvidence(input: AppendPlanningEvidenceInput): PlanningEvidencePack {
  if (input.current && input.current.reviewOperationId !== input.reviewOperationId) {
    throw new Error("Planning evidence cannot cross Review operations.");
  }

  const capturedAt = input.capturedAt ?? new Date().toISOString();
  const query = input.query.trim();
  const citationIds = extractCitationIdsFromPlanningAnswer(input.answer);
  const call: PlanningEvidenceCall = {
    evidenceId: input.evidenceId ?? createPlanningEvidenceId(),
    tool: input.tool,
    query,
    answer: input.answer,
    ...(input.sessionId?.trim() ? { sessionId: input.sessionId.trim() } : undefined),
    capturedAt,
    answerChars: unicodeCharacterCount(input.answer),
    citationIds: Object.freeze(citationIds),
  };

  const calls = [...(input.current?.calls ?? []), call];

  const retained = retainReferencedCitationEnvelopes(
    [...(input.current?.citations ?? []), ...(input.citations ?? [])],
    calls
  );
  return finalizePlanningEvidencePack({
    reviewOperationId: input.reviewOperationId,
    capturedAt,
    skillIds: input.current?.skillIds,
    guidanceChars: input.current?.guidanceChars,
    calls: retained.calls,
    citations: retained.citations,
    truncated: Boolean(input.current?.truncated),
  });
}

export function withPlanningEvidenceSkillBinding(
  pack: PlanningEvidencePack,
  skillIds: readonly string[] | undefined,
  guidanceChars: number | undefined
): PlanningEvidencePack {
  const nextIds = skillIds?.length ? [...skillIds] : undefined;
  const nextChars = typeof guidanceChars === "number" ? guidanceChars : undefined;
  const sameIds =
    (pack.skillIds?.length ?? 0) === (nextIds?.length ?? 0) &&
    (pack.skillIds ?? []).every((id, index) => id === nextIds?.[index]);
  if (sameIds && pack.guidanceChars === nextChars) return pack;
  return finalizePlanningEvidencePack({
    reviewOperationId: pack.reviewOperationId,
    capturedAt: pack.capturedAt,
    skillIds: nextIds,
    guidanceChars: nextChars,
    calls: pack.calls,
    citations: pack.citations,
    truncated: pack.truncated,
  });
}

/** First occurrence wins by citation UUID, matching the turn collector. */
export function unionGroundingCitations(
  taskCitations: readonly Citation[] | undefined,
  planningCitations: readonly Citation[] | undefined
): Citation[] | undefined {
  const merged: Citation[] = [];
  const seenIds = new Set<string>();
  for (const citation of [...(taskCitations ?? []), ...(planningCitations ?? [])]) {
    if (!citation || typeof citation !== "object") continue;
    const ids = extractIdsFromCitation(citation);
    if (ids.length > 0 && ids.every((id) => seenIds.has(id))) continue;
    merged.push(citation);
    for (const id of ids) seenIds.add(id);
  }
  return merged.length > 0 ? merged : undefined;
}

export function renderPlanningEvidencePack(
  pack: PlanningEvidencePack | undefined
): string | undefined {
  if (!pack || pack.calls.length === 0) return undefined;
  const calls = pack.calls.flatMap((call, index) => [
    `<research_call index="${index + 1}" tool="${call.tool}">`,
    "<query>",
    call.query,
    "</query>",
    "<answer>",
    call.answer,
    "</answer>",
    "</research_call>",
  ]);
  return [
    "PLANNING RESEARCH EVIDENCE (locked for this approved plan)",
    "Use these results as the primary factual evidence for every fact they cover.",
    "Preserve citation IDs embedded in the answers and declare only citations for values actually used on a slide.",
    "Do not repeat research for covered facts. If an approved Review change requires a factual input absent here, research only that gap.",
    "If task grounding and planning evidence appear inconsistent, preserve their stated periods/as-of dates and do not silently combine incompatible values.",
    "",
    "<planning_evidence>",
    ...calls,
    "</planning_evidence>",
  ].join("\n");
}
